import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "sim-card-stats" }
const _hoisted_2 = { class: "stats-overview" }
const _hoisted_3 = { class: "card-header" }
const _hoisted_4 = { class: "chart-container" }
const _hoisted_5 = { class: "chart-legend" }
const _hoisted_6 = { class: "legend-text" }
const _hoisted_7 = { class: "card-header" }
const _hoisted_8 = { class: "chart-container" }
const _hoisted_9 = { class: "chart-legend" }
const _hoisted_10 = { class: "legend-text" }
const _hoisted_11 = { class: "card-header" }
const _hoisted_12 = { class: "chart-container" }
const _hoisted_13 = { class: "chart-legend" }
const _hoisted_14 = { class: "legend-text" }
const _hoisted_15 = { class: "traffic-stats" }
const _hoisted_16 = { class: "chart-container" }
const _hoisted_17 = { class: "top5-table" }
const _hoisted_18 = { class: "billing-group-stats" }
const _hoisted_19 = { class: "card-header" }
const _hoisted_20 = { class: "billing-group-table" }
const _hoisted_21 = { class: "operator-stats" }
const _hoisted_22 = { class: "chart-container" }

import { ref, onMounted, nextTick, computed } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { Refresh, List } from "@element-plus/icons-vue";
import apiClient from "@/services/apiClient";
import { API_URLS } from "@/config";

// 路由
interface StatsData {
  totalCards: number;
  expiringCards: number;
  activeCards: number;
  functionStatus: {
    normal?: number;
    suspended?: number;
    other?: number;
  };
  businessStatus: {
    inUse?: number;
    testing?: number;
    silent?: number;
  };
  expiringStatus: {
    july?: number;
    august?: number;
    september?: number;
  };
  operatorDistribution: Record<string, number>;
  monthlyTraffic: Array<{
    date: string;
    traffic: number;
  }>;
  top5Usage: Array<{
    iccid: string;
    usage: number;
  }>;
}

// 响应式数据

export default /*@__PURE__*/_defineComponent({
  __name: 'SimCardStats',
  setup(__props) {

const router = useRouter();

// 类型定义
const loading = ref(false);
const statsData = ref<StatsData>({
  totalCards: 0,
  expiringCards: 0,
  activeCards: 0,
  functionStatus: {},
  businessStatus: {},
  expiringStatus: {},
  operatorDistribution: {},
  monthlyTraffic: [],
  top5Usage: [],
});

// 计费组相关数据
const selectedBillingGroup = ref("all");
const billingGroups = ref<any[]>([]);
const billingGroupData = ref<any[]>([]);

// 图表引用
const functionStatusChart = ref();
const expiringChart = ref();
const businessStatusChart = ref();
const trafficChart = ref();
const operatorChart = ref();

// 图表实例
let functionStatusChartInstance: any = null;
let expiringChartInstance: any = null;
let businessStatusChartInstance: any = null;
let trafficChartInstance: any = null;
let operatorChartInstance: any = null;

// 计算属性
const functionStatusData = computed(() => {
  const data = statsData.value.functionStatus;
  return [
    { name: "正常", value: data.normal || 0, color: "#67C23A" },
    { name: "停机", value: data.suspended || 0, color: "#E6A23C" },
    { name: "其它", value: data.other || 0, color: "#409EFF" },
  ];
});

const expiringData = computed(() => {
  const data = statsData.value.expiringStatus;
  return [
    { name: "2025年07月", value: data.july || 0, color: "#67C23A" },
    { name: "2025年08月", value: data.august || 0, color: "#409EFF" },
    { name: "2025年09月", value: data.september || 0, color: "#E6A23C" },
  ];
});

const businessStatusData = computed(() => {
  const data = statsData.value.businessStatus;
  return [
    { name: "正使用", value: data.inUse || 0, color: "#67C23A" },
    { name: "测试期", value: data.testing || 0, color: "#409EFF" },
    { name: "沉默期", value: data.silent || 0, color: "#E6A23C" },
  ];
});

const top5Data = computed(() => {
  return statsData.value.top5Usage.map((item, index) => ({
    rank: index + 1,
    iccid: item.iccid,
    usage: item.usage,
  }));
});

// 方法
const fetchStatsData = async () => {
  try {
    loading.value = true;
    const response = await apiClient.get(API_URLS.SIM_CARDS_STATS);

    if (response.data.code === 0) {
      statsData.value = response.data.data;
      await nextTick();
      initCharts();
    } else {
      ElMessage.error(response.data.message || "获取统计数据失败");
    }
  } catch (error) {
    console.error("获取统计数据失败:", error);
    ElMessage.error("获取统计数据失败");
  } finally {
    loading.value = false;
  }
};

const initCharts = () => {
  // 动态导入echarts以避免SSR问题
  import("echarts").then((echarts) => {
    // 初始化功能状态图表
    if (functionStatusChart.value) {
      functionStatusChartInstance = echarts.init(functionStatusChart.value);
      const functionStatusOption = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} ({d}%)",
        },
        series: [
          {
            type: "pie",
            radius: ["40%", "70%"],
            data: functionStatusData.value.map((item) => ({
              name: item.name,
              value: item.value,
              itemStyle: { color: item.color },
            })),
          },
        ],
      };
      functionStatusChartInstance.setOption(functionStatusOption);
    }

    // 初始化到期图表
    if (expiringChart.value) {
      expiringChartInstance = echarts.init(expiringChart.value);
      const expiringOption = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} ({d}%)",
        },
        series: [
          {
            type: "pie",
            radius: ["40%", "70%"],
            data: expiringData.value.map((item) => ({
              name: item.name,
              value: item.value,
              itemStyle: { color: item.color },
            })),
          },
        ],
      };
      expiringChartInstance.setOption(expiringOption);
    }

    // 初始化业务状态图表
    if (businessStatusChart.value) {
      businessStatusChartInstance = echarts.init(businessStatusChart.value);
      const businessStatusOption = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} ({d}%)",
        },
        series: [
          {
            type: "pie",
            radius: ["40%", "70%"],
            data: businessStatusData.value.map((item) => ({
              name: item.name,
              value: item.value,
              itemStyle: { color: item.color },
            })),
          },
        ],
      };
      businessStatusChartInstance.setOption(businessStatusOption);
    }

    // 初始化流量图表
    if (trafficChart.value) {
      trafficChartInstance = echarts.init(trafficChart.value);
      const trafficData = statsData.value.monthlyTraffic || [];
      const trafficOption = {
        tooltip: {
          trigger: "axis",
        },
        xAxis: {
          type: "category",
          data: trafficData.map((item) => item.date),
        },
        yAxis: {
          type: "value",
          name: "流量",
        },
        series: [
          {
            type: "line",
            data: trafficData.map((item) => item.traffic),
            smooth: true,
          },
        ],
      };
      trafficChartInstance.setOption(trafficOption);
    }

    // 初始化运营商图表
    if (operatorChart.value) {
      operatorChartInstance = echarts.init(operatorChart.value);
      const operatorData = statsData.value.operatorDistribution || {};
      const operatorOption = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} ({d}%)",
        },
        series: [
          {
            type: "pie",
            radius: "50%",
            data: Object.entries(operatorData).map(([name, value]) => ({
              name,
              value,
            })),
          },
        ],
      };
      operatorChartInstance.setOption(operatorOption);
    }
  });
};

const refreshData = () => {
  fetchStatsData();
};

const goToSimCardList = () => {
  router.push("/simCardList");
};

// 计费组相关方法
const fetchBillingGroups = async () => {
  try {
    const response = await apiClient.get(API_URLS.SIM_CARDS_BILLING_GROUPS);

    if (response.data.code === 0) {
      billingGroups.value = response.data.data.billingGroups || [];
      billingGroupData.value = response.data.data.billingGroups || [];
    }
  } catch (error) {
    console.error("获取计费组数据失败:", error);
  }
};

const handleBillingGroupChange = (value: string) => {
  if (value === "all") {
    billingGroupData.value = billingGroups.value;
  } else {
    billingGroupData.value = billingGroups.value.filter(
      (group) => group.code === value
    );
  }
};

// 生命周期
onMounted(() => {
  fetchStatsData();
  fetchBillingGroups();

  // 监听窗口大小变化，重新调整图表
  window.addEventListener("resize", () => {
    functionStatusChartInstance?.resize();
    expiringChartInstance?.resize();
    businessStatusChartInstance?.resize();
    trafficChartInstance?.resize();
    operatorChartInstance?.resize();
  });
});

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_2, [
      _createVNode(_component_el_row, { gutter: 20 }, {
        default: _withCtx(() => [
          _createVNode(_component_el_col, { span: 8 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_card, { class: "stats-card" }, {
                header: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _cache[1] || (_cache[1] = _createElementVNode("span", null, "SIM卡功能状态", -1)),
                    _createVNode(_component_el_tag, { type: "info" }, {
                      default: _withCtx(() => [
                        _createTextVNode("总数: " + _toDisplayString(statsData.value.totalCards || 0), 1)
                      ]),
                      _: 1
                    })
                  ])
                ]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", {
                      ref_key: "functionStatusChart",
                      ref: functionStatusChart,
                      class: "chart"
                    }, null, 512)
                  ]),
                  _createElementVNode("div", _hoisted_5, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(functionStatusData.value, (item) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: "legend-item",
                        key: item.name
                      }, [
                        _createElementVNode("span", {
                          class: "legend-color",
                          style: _normalizeStyle({ backgroundColor: item.color })
                        }, null, 4),
                        _createElementVNode("span", _hoisted_6, _toDisplayString(item.name) + " " + _toDisplayString(item.value) + "张", 1)
                      ]))
                    }), 128))
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 8 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_card, { class: "stats-card" }, {
                header: _withCtx(() => [
                  _createElementVNode("div", _hoisted_7, [
                    _cache[2] || (_cache[2] = _createElementVNode("span", null, "即将到期卡", -1)),
                    _createVNode(_component_el_tag, { type: "warning" }, {
                      default: _withCtx(() => [
                        _createTextVNode("三个月内: " + _toDisplayString(statsData.value.expiringCards || 0), 1)
                      ]),
                      _: 1
                    })
                  ])
                ]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", {
                      ref_key: "expiringChart",
                      ref: expiringChart,
                      class: "chart"
                    }, null, 512)
                  ]),
                  _createElementVNode("div", _hoisted_9, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(expiringData.value, (item) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: "legend-item",
                        key: item.name
                      }, [
                        _createElementVNode("span", {
                          class: "legend-color",
                          style: _normalizeStyle({ backgroundColor: item.color })
                        }, null, 4),
                        _createElementVNode("span", _hoisted_10, _toDisplayString(item.name) + " " + _toDisplayString(item.value) + "张", 1)
                      ]))
                    }), 128))
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 8 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_card, { class: "stats-card" }, {
                header: _withCtx(() => [
                  _createElementVNode("div", _hoisted_11, [
                    _cache[3] || (_cache[3] = _createElementVNode("span", null, "SIM卡业务状态", -1)),
                    _createVNode(_component_el_tag, { type: "success" }, {
                      default: _withCtx(() => [
                        _createTextVNode("正常使用: " + _toDisplayString(statsData.value.activeCards || 0), 1)
                      ]),
                      _: 1
                    })
                  ])
                ]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("div", {
                      ref_key: "businessStatusChart",
                      ref: businessStatusChart,
                      class: "chart"
                    }, null, 512)
                  ]),
                  _createElementVNode("div", _hoisted_13, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(businessStatusData.value, (item) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: "legend-item",
                        key: item.name
                      }, [
                        _createElementVNode("span", {
                          class: "legend-color",
                          style: _normalizeStyle({ backgroundColor: item.color })
                        }, null, 4),
                        _createElementVNode("span", _hoisted_14, _toDisplayString(item.name) + " " + _toDisplayString(item.value) + "张", 1)
                      ]))
                    }), 128))
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ])), [
      [_directive_loading, loading.value]
    ]),
    _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_15, [
      _createVNode(_component_el_row, { gutter: 20 }, {
        default: _withCtx(() => [
          _createVNode(_component_el_col, { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_card, { class: "stats-card" }, {
                header: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("div", { class: "card-header" }, [
                    _createElementVNode("span", null, "本月GPRS流量"),
                    _createElementVNode("span", { class: "subtitle" }, "本月每日流量")
                  ], -1)
                ]))]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", {
                      ref_key: "trafficChart",
                      ref: trafficChart,
                      class: "chart"
                    }, null, 512)
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_card, { class: "stats-card" }, {
                header: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                  _createElementVNode("div", { class: "card-header" }, [
                    _createElementVNode("span", null, "本月流量消耗TOP5")
                  ], -1)
                ]))]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_17, [
                    _createVNode(_component_el_table, {
                      data: top5Data.value,
                      stripe: ""
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_table_column, {
                          prop: "rank",
                          label: "序号",
                          width: "80"
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "iccid",
                          label: "SIM卡号"
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "usage",
                          label: "使用流量(M)"
                        })
                      ]),
                      _: 1
                    }, 8, ["data"])
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ])), [
      [_directive_loading, loading.value]
    ]),
    _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_18, [
      _createVNode(_component_el_card, { class: "stats-card" }, {
        header: _withCtx(() => [
          _createElementVNode("div", _hoisted_19, [
            _cache[6] || (_cache[6] = _createElementVNode("span", null, "计费组统计", -1)),
            _createVNode(_component_el_select, {
              modelValue: selectedBillingGroup.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedBillingGroup).value = $event)),
              placeholder: "选择计费组",
              onChange: handleBillingGroupChange,
              style: {"width":"200px"}
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_option, {
                  label: "全部计费组",
                  value: "all"
                }),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(billingGroups.value, (group) => {
                  return (_openBlock(), _createBlock(_component_el_option, {
                    key: group.code,
                    label: group.name,
                    value: group.code
                  }, null, 8, ["label", "value"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ])
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_20, [
            _createVNode(_component_el_table, {
              data: billingGroupData.value,
              stripe: ""
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_table_column, {
                  prop: "code",
                  label: "计费组代码",
                  width: "200"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "name",
                  label: "计费组名称",
                  width: "200"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "trafficPoolCode",
                  label: "流量池代码",
                  width: "150"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "trafficPoolName",
                  label: "流量池名称",
                  width: "200"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "billingMode",
                  label: "计费模式",
                  width: "100"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "availableCards",
                  label: "计费组可用卡数",
                  width: "120"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "activatedCards",
                  label: "计费组激活卡数",
                  width: "120"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "poolAvailableCards",
                  label: "流量池可用卡数",
                  width: "120"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "poolActivatedCards",
                  label: "流量池激活卡数",
                  width: "120"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "totalTrafficMB",
                  label: "流量池总流量(MB)",
                  width: "140"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "usedTrafficMB",
                  label: "流量池已用流量(MB)",
                  width: "140"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "usedTrafficPercentage",
                  label: "流量池已用流量百分比",
                  width: "150"
                }, {
                  default: _withCtx(({ row }) => [
                    _createTextVNode(_toDisplayString(row.usedTrafficPercentage) + "% ", 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["data"])
          ])
        ]),
        _: 1
      })
    ])), [
      [_directive_loading, loading.value]
    ]),
    _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_21, [
      _createVNode(_component_el_card, { class: "stats-card" }, {
        header: _withCtx(() => [...(_cache[7] || (_cache[7] = [
          _createElementVNode("div", { class: "card-header" }, [
            _createElementVNode("span", null, "运营商分布")
          ], -1)
        ]))]),
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_22, [
            _createElementVNode("div", {
              ref_key: "operatorChart",
              ref: operatorChart,
              class: "chart"
            }, null, 512)
          ])
        ]),
        _: 1
      })
    ])), [
      [_directive_loading, loading.value]
    ])
  ]))
}
}

})